import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { api } from '@/lib/api';
import { toast } from '@/hooks/use-toast';
import { CampaignArtifacts } from '@/components/campaigns/CampaignArtifacts';
import { SOURCES } from '@/lib/types';
import type {
  AppRecord,
  Campaign,
  CampaignInput,
  CampaignSource,
  CampaignType,
} from '@/lib/types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  apps: AppRecord[];
}

function blank(appId: number | undefined): CampaignInput {
  return {
    name: '',
    appId: appId ?? 0,
    type: 'redirect',
    source: 'instagram',
    deepLink: '',
    expiresAt: '',
    notes: '',
  };
}

function trimToUndefined(v: string | undefined): string | undefined {
  const t = v?.trim();
  return t ? t : undefined;
}

export function CampaignFormDialog({ open, onOpenChange, apps }: Props) {
  const qc = useQueryClient();
  const [form, setForm] = useState<CampaignInput>(() => blank(apps[0]?.id));
  const [created, setCreated] = useState<Campaign | null>(null);

  useEffect(() => {
    if (open) {
      setForm(blank(apps[0]?.id));
      setCreated(null);
    }
  }, [open, apps]);

  const save = useMutation({
    mutationFn: async () => {
      const payload: CampaignInput = {
        ...form,
        deepLink: trimToUndefined(form.deepLink),
        expiresAt: trimToUndefined(form.expiresAt),
        notes: trimToUndefined(form.notes),
      };
      const { data } = await api.post<Campaign>('/v1/campaigns', payload);
      return data;
    },
    onSuccess: (campaign) => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      toast({ title: 'Campaign created' });
      setCreated(campaign);
      // Dialog stays open on the success view; the user closes when ready.
    },
    onError: () =>
      toast({ title: 'Could not create campaign', variant: 'destructive' }),
  });

  function update<K extends keyof CampaignInput>(key: K, value: CampaignInput[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!form.name.trim() || !form.appId) return;
    save.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {created ? `Campaign created — ${created.name}` : 'New campaign'}
          </DialogTitle>
          <DialogDescription>
            {created
              ? 'Copy the links you need; you can revisit them anytime from the campaign page.'
              : 'We generate the opaque tracking code automatically.'}
          </DialogDescription>
        </DialogHeader>
        {created ? (
          <div className="grid gap-4">
            <CampaignArtifacts campaign={created} />
            <DialogFooter>
              <Button type="button" onClick={() => onOpenChange(false)}>
                Done
              </Button>
            </DialogFooter>
          </div>
        ) : (
        <form onSubmit={onSubmit} className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
              placeholder="Spring Sale — IG Story — Mar 2026"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-2">
              <Label htmlFor="appId">App</Label>
              <Select
                value={form.appId ? String(form.appId) : ''}
                onValueChange={(v) => update('appId', Number(v))}
              >
                <SelectTrigger id="appId">
                  <SelectValue placeholder="Select an app" />
                </SelectTrigger>
                <SelectContent position="popper" align="start">
                  {apps.map((a) => (
                    <SelectItem key={a.id} value={String(a.id)}>
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="type">Type</Label>
              <Select
                value={form.type}
                onValueChange={(v) => update('type', v as CampaignType)}
              >
                <SelectTrigger id="type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent position="popper" align="start">
                  <SelectItem value="redirect">Redirect</SelectItem>
                  <SelectItem value="landing">Landing</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-2">
              <Label htmlFor="source">Source</Label>
              <Select
                value={form.source}
                onValueChange={(v) => update('source', v as CampaignSource)}
              >
                <SelectTrigger id="source">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent position="popper" align="start">
                  {SOURCES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="expiresAt">Expiry</Label>
              <Input
                id="expiresAt"
                type="date"
                value={form.expiresAt ?? ''}
                onChange={(e) => update('expiresAt', e.target.value)}
              />
            </div>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="deepLink">Deep-link target (optional)</Label>
            <Input
              id="deepLink"
              value={form.deepLink ?? ''}
              onChange={(e) => update('deepLink', e.target.value)}
              placeholder="items/12345"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="notes">Notes</Label>
            <Input
              id="notes"
              value={form.notes ?? ''}
              onChange={(e) => update('notes', e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Generating…' : 'Generate'}
            </Button>
          </DialogFooter>
        </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
